/**
 * TaskColumnsEditorItem.jsx
 *
 * A draggable list item representing a configurable column in a task view editor.
 * Supports toggling visibility via checkbox and reordering vai drag-and-drop.
 * Utilizes Material UI for the UI layout and styling, and react-dnd for drag-and-drop logic.
 */

import { memo, useCallback } from "react";
import PropTypes from "prop-types";
import { useDrag, useDrop } from "react-dnd";
import Checkbox from "@mui/material/Checkbox";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { columnTypeToIcon } from "../../helpers/columnUtils";
import DragHandle from "../../common/DragHandle";

/**
 * Enum that defines the internal item types used for drag-and-drop.
 * Used by react-dnd to distinguish draggable column items.
 *
 * @readonly
 * @enum {string}
 */
const ItemTypes = {
  COLUMN: 'COLUMN',
};

/**
 * @typedef {Object} TaskColumn
 * Represents a single column item in the task editor UI.
 *
 * @property {boolean} checked - Whether the column is visible (selected).
 * @property {string|number} fieldId - Unique identifier used in drag logic and state updates.
 * @property {React.ReactNode} name - The label to display for the column.
 * @property {number} type - Numeric identifier used to determine which icon to render.
 */

/**
 * @typedef {Object} TaskColumnsEditorItemProps
 * @property {TaskColumn} column - Column data for the item.
 * @property {boolean} [disabled] - If true, disables interactions (dragging and toggling).
 * @property {boolean} [disableDnd] - If true, disables drag-and-drop.
 * @property {number} index - Index of the item in the list (used for reordering logic).
 * @property {(fieldId: string|number, checked: boolean) => void} onChange - Called when the visibility checkbox is toggled for this column.
 * @property {(fromIndex: number, toIndex: number) => void} onMove - Called when the item is reordered via drag-and-drop.
 */

/**
 * TaskColumnsEditorItem
 *
 * A single list item in the task view editor.
 * Represents a configurable column that can be toggled via checkbox and reordered using drag-and-drop.
 * Integrates with react-dnd for reordering, and Material UI for layout and interactivity.
 *
 * @component
 * @param {TaskColumnsEditorItemProps} props
 * @returns {JSX.Element}
 */
const TaskColumnsEditorItem = ({
  column,
  disabled,
  disableDnd,
  index,
  onChange,
  onMove,
}) => {
  const { checked, fieldId, name, type } = column;

  const dndAllowed = !disableDnd;

  const IconComponent = columnTypeToIcon[type];

  /**
   * Register this item as a draggable source.
   * The returned dragRef is passed to the drag handle element.
   * The `isDragging` state is used to control the item's opacity, providing visual feedback during dragging.
   */
  const [{ isDragging }, dragRef] = useDrag({
    type: ItemTypes.COLUMN,
    item: { fieldId, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  /**
   * Register this item as a drop target for other COLUMN items.
   * `hover()` is called repeatedly as a dragged item moves over this one.
   * When appropriate, triggers `onMove()` to reorder the list.
   */
  const [{ isHovered }, dropRef] = useDrop({
    accept: ItemTypes.COLUMN,
    collect: (monitor) => ({
      isHovered: monitor.isOver({ shallow: true }),
    }),
    drop: (draggedItem, monitor) => {
      // Proceed only if the hover target is not a nested element (using shallow hover check)
      if (!monitor.isOver({ shallow: true })) return;

      // Ignore if dragging over the same item � no reordering needed
      if (draggedItem.fieldId === fieldId) return;

      // Ignore if index hasn't changed (safety check)
      if (draggedItem.index === index) return;

      onMove(draggedItem.index, index);
    },
  });

  /**
   * Conditionally applies the drop target ref to the DOM node,
   * preventing drag-over behavior on disabled list items.
   *
   * @param {HTMLElement | null} node - The DOM node for the list item.
   * @returns {void}
   */
  const applyDropRefIfEnabled = useCallback((node) => {
    if (node && !disabled && dndAllowed) {
      dropRef(node);
    }
  }, [disabled, dropRef]);

  // Renders the list item with a drag handle, an optional column icon, a label, and a checkbox for toggling visibility.
  return (
    <ListItem
      data-field-id={fieldId}
      divider
      ref={applyDropRefIfEnabled}
      secondaryAction={
        <Checkbox
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(fieldId, e.target.checked)}
          value={fieldId}
        />
      }
      sx={{
        transition: 'background-color 80ms ease',
        backgroundColor: isHovered
          ? "action.hover"
          : isDragging
            ? "action.selected"
            : undefined,
      }}
    >
      <ListItemIcon>
        {!disabled && dndAllowed && <DragHandle ref={dragRef} />}
        {IconComponent && <IconComponent />}
      </ListItemIcon>
      <ListItemText primary={name} />
    </ListItem>
  );
};

/**
 * PropType definition for a single task column object.
 * Used to validate the shape of the `column` prop in TaskColumnsEditorItem.
 */
const TaskColumnPropType = PropTypes.shape({
  checked: PropTypes.bool.isRequired,
  fieldId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.node.isRequired,
  type: PropTypes.number.isRequired,
});

TaskColumnsEditorItem.propTypes = {
  column: TaskColumnPropType.isRequired,
  disabled: PropTypes.bool,
  disableDnd: PropTypes.bool,
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
};

TaskColumnsEditorItem.displayName = "TaskColumnsEditorItem";

export { ItemTypes };

export default memo(TaskColumnsEditorItem);
